import { UI_CONSTANTS, ERROR_MESSAGES, CLOTHING_SIZES, SHOE_SIZE_RANGE } from '../constants/app';

/**
 * Input validation utilities
 */

/**
 * Sanitize string input to prevent XSS
 * @param {string} input - Input string to sanitize
 * @returns {string} Sanitized string
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .trim()
    .substring(0, UI_CONSTANTS.MAX_INPUT_LENGTH);
};

/**
 * Validate employee ID format (4 digits)
 * @param {string} employeeId - Employee ID to validate
 * @returns {Object} Validation result
 */
export const validateEmployeeId = (employeeId) => {
  const sanitized = sanitizeInput(employeeId);
  
  if (!sanitized) {
    return { isValid: false, error: ERROR_MESSAGES.VALIDATION_REQUIRED };
  }
  
  const pattern = new RegExp(`^\\d{${UI_CONSTANTS.EMPLOYEE_ID_LENGTH}}$`);
  if (!pattern.test(sanitized)) {
    return { isValid: false, error: `Employee ID must be exactly ${UI_CONSTANTS.EMPLOYEE_ID_LENGTH} digits` };
  }
  
  return { isValid: true, value: sanitized };
};

/**
 * Validate employee ID against local employee data
 * @param {string} employeeId - Employee ID to validate
 * @param {Array} employeeList - Array of employee data
 * @returns {Object} Validation result with employee data if found
 */
export const validateEmployeeIdWithData = (employeeId, employeeList = []) => {
  // First validate the format
  const formatValidation = validateEmployeeId(employeeId);
  if (!formatValidation.isValid) {
    return formatValidation;
  }
  
  // Then check if employee exists in the local data
  const employee = employeeList.find(emp => emp.emp_id === formatValidation.value);
  
  if (!employee) {
    return { isValid: false, error: 'Employee not found' };
  }
  
  return { 
    isValid: true, 
    value: formatValidation.value,
    employeeData: employee 
  };
};

/**
 * Validate username format
 * @param {string} username - Username to validate
 * @returns {Object} Validation result
 */
export const validateUsername = (username) => {
  const sanitized = sanitizeInput(username);
  
  if (!sanitized) {
    return { isValid: false, error: ERROR_MESSAGES.VALIDATION_REQUIRED };
  }
  
  if (sanitized.length < UI_CONSTANTS.MIN_USERNAME_LENGTH) {
    return { isValid: false, error: `Username must be at least ${UI_CONSTANTS.MIN_USERNAME_LENGTH} characters` };
  }
  
  if (sanitized.length > UI_CONSTANTS.MAX_USERNAME_LENGTH) {
    return { isValid: false, error: `Username must be less than ${UI_CONSTANTS.MAX_USERNAME_LENGTH} characters` };
  }
  
  if (!/^[a-zA-Z0-9_.-]+$/.test(sanitized)) {
    return { isValid: false, error: 'Username can only contain letters, numbers, dots, hyphens, and underscores' };
  }
  
  return { isValid: true, value: sanitized };
};

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {Object} Validation result
 */
export const validatePassword = (password) => {
  if (!password) {
    return { isValid: false, error: ERROR_MESSAGES.VALIDATION_REQUIRED };
  }
  
  if (password.length > UI_CONSTANTS.MAX_PASSWORD_LENGTH) {
    return { isValid: false, error: `Password must be less than ${UI_CONSTANTS.MAX_PASSWORD_LENGTH} characters` };
  }
  
  return { isValid: true, value: password };
};

/**
 * Validate search term
 * @param {string} searchTerm - Search term to validate
 * @returns {Object} Validation result
 */
export const validateSearchTerm = (searchTerm) => {
  const sanitized = sanitizeInput(searchTerm);
  
  if (sanitized.length > UI_CONSTANTS.MAX_SEARCH_LENGTH) {
    return { isValid: false, error: 'Search term too long' };
  }
  
  return { isValid: true, value: sanitized };
};

/**
 * Validate goodie size
 * @param {string} size - Size to validate
 * @param {string} goodieType - Type of goodie (for specific validation)
 * @returns {Object} Validation result
 */
export const validateGoodieSize = (size, goodieType = '') => {
  const sanitized = sanitizeInput(size);
  
  if (!sanitized) {
    return { isValid: false, error: ERROR_MESSAGES.VALIDATION_REQUIRED };
  }
  
  // For shoes, validate numeric sizes
  if (goodieType.toLowerCase().includes('shoe')) {
    const numericSize = parseInt(sanitized, 10);
    if (isNaN(numericSize) || numericSize < SHOE_SIZE_RANGE.MIN || numericSize > SHOE_SIZE_RANGE.MAX) {
      return { isValid: false, error: `Shoe size must be between ${SHOE_SIZE_RANGE.MIN} and ${SHOE_SIZE_RANGE.MAX}` };
    }
    return { isValid: true, value: sanitized };
  }
  
  // For clothing, validate standard sizes
  const upperSize = sanitized.toUpperCase();
  if (!CLOTHING_SIZES.includes(upperSize) && !/^\d{1,2}$/.test(sanitized)) {
    return { isValid: false, error: `Invalid size. Use: ${CLOTHING_SIZES.join(', ')} or numeric sizes` };
  }
  
  return { isValid: true, value: upperSize };
};

/**
 * Validate API response data
 * @param {any} data - Data to validate
 * @param {string} expectedType - Expected data type
 * @returns {boolean} Validation result
 */
export const validateApiResponse = (data, expectedType = 'object') => {
  if (!data) return false;
  
  switch (expectedType) {
    case 'array':
      return Array.isArray(data);
    case 'object':
      return typeof data === 'object' && !Array.isArray(data);
    case 'string':
      return typeof data === 'string';
    case 'number':
      return typeof data === 'number' && !isNaN(data);
    default:
      return true;
  }
};
